import React, { Fragment, useMemo, useState } from 'react';
import {
  OfflineCurrentCustomerMetadata,
  OfflineTaskPayloadMap,
  OfflineTaskType,
  PersistedTask,
  TripCheckInLocation,
} from '@zonofi/zono-money-store';
import { CheckInCheckOutTask } from './check-in-check-out-card';
import { SyncTransactionCard } from './sync-transaction-card';
import { TouchableOpacity } from '../common';
import { Switcher } from '../components';
import { addTaskToQueue } from '../queue/offline';

export type TaskItem = Omit<PersistedTask, 'customerMetadata'>;

interface SyncTaskItemCardProps {
  task: TaskItem | TaskItem[];
  customer: OfflineCurrentCustomerMetadata;
}

export const SyncTaskItemCard: React.FC<SyncTaskItemCardProps> = ({
  task,
  customer,
}) => {
  const initialConfirmed = useMemo(() => {
    if (
      !Array.isArray(task) &&
      task.type === OfflineTaskType.PLACE_ORDER &&
      Object.keys(task.payload).length > 0
    ) {
      const payload =
        task.payload as OfflineTaskPayloadMap[OfflineTaskType.PLACE_ORDER];
      return payload?.askForConfirmation ?? false;
    }
    return false;
  }, [task]);

  const [isConfirmed, setIsConfirmed] = useState(initialConfirmed);
  const onConfirmPress = async () => {
    setIsConfirmed((prev) => !prev);
    await addTaskToQueue({
      type: OfflineTaskType.PLACE_ORDER,
      customer: customer,
      payload: { askForConfirmation: !isConfirmed },
    });
  };

  if (Array.isArray(task)) {
    return (
      <div className="transactions-list">
        {task.map((x, idx) => (
          <Fragment key={x.id}>
            <SyncTaskItemCard task={x} customer={customer} />
            {idx !== task.length - 1 && (
              <div className="transactions-list-divider" />
            )}
          </Fragment>
        ))}
      </div>
    );
  }

  switch (task.type) {
    case OfflineTaskType.CHECK_IN:
    case OfflineTaskType.CHECK_OUT:
      return (
        <CheckInCheckOutTask
          key={task.id}
          type={task.type}
          payload={task.payload as TripCheckInLocation}
          timestamp={task.timestamp}
        />
      );
    case OfflineTaskType.COLLECT_PAYMENT:
      return (
        <SyncTransactionCard
          type={task.type}
          customer={customer}
          payload={task.payload}
        />
      );
    case OfflineTaskType.PLACE_ORDER:
      return (
        <>
          <SyncTransactionCard
            type={task.type}
            customer={customer}
            payload={task.payload}
          />

          <TouchableOpacity
            className="sync-order-confirmation-view"
            onPress={onConfirmPress}
          >
            <span className="sync-order-confirmation-view--text font-semibold">
              {'Ask for confirmation'}
            </span>
            <Switcher
              checked={isConfirmed}
              switcherStyles={{
                switchSize: { width: 32, height: 22 },
                thumbSize: { width: 16, height: 16 },
                checkedTransform: 'translateX(10px)',
                noSelectBg: '#f0f0f0',
                thumbColor: isConfirmed ? '#fff' : '#8c8c8c',
              }}
            />
          </TouchableOpacity>
        </>
      );
    default:
      return null;
  }
};
